/**
 * Services.tsx — /services
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Asymmetric layout, generous whitespace, editorial card style
 * - Muted earth tones, Playfair Display headings, Source Sans 3 body
 * - Available services linkable, upcoming ones marked clearly
 */

import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Home, Building2, Users, TrendingUp, Clock } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const RE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const CS_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/cs-hero-office_4a9dd604.jpg";

const SERVICES = [
  {
    number: "01",
    icon: Home,
    title: "Real Estate",
    subtitle: "Find, negotiate, and close",
    description:
      "From the first search to the keys in your hand. I help you find the right property in Brașov, negotiate the price, navigate the legal process, and settle in — without the usual surprises.",
    highlights: ["Property search & shortlisting", "Price negotiation", "Legal & notary assistance", "Post-purchase support"],
    href: "/services/real-estate",
    img: RE_IMG,
    available: true,
    cta: "Explore Real Estate",
  },
  {
    number: "02",
    icon: Building2,
    title: "Company Setup",
    subtitle: "Open a Romanian SRL in 14 working days",
    description:
      "I handle the full incorporation process for your Romanian SRL — from documentation to fiscal registration (CUI) and ongoing compliance. You focus on your business; I handle the paperwork.",
    highlights: ["SRL incorporation", "Fiscal registration (CUI)", "Bank account assistance", "Ongoing tax compliance"],
    href: "/services/company-setup",
    img: CS_IMG,
    available: true,
    cta: "Explore Company Setup",
  },
  {
    number: "03",
    icon: Users,
    title: "Relocation Support",
    subtitle: "Move to Brașov without the guesswork",
    description:
      "Moving to a new country is more than finding an apartment. I help you navigate residence registration, banking, schools, healthcare, and everything else that makes a city feel like home.",
    highlights: ["Residence registration", "Bank account opening", "Schools & healthcare", "Utilities & services"],
    href: "/contact",
    img: null,
    available: false,
    cta: "Enquire",
  },
  {
    number: "04",
    icon: TrendingUp,
    title: "Business Opportunities",
    subtitle: "Invest and operate in Brașov",
    description:
      "Brașov is one of Romania's fastest-growing cities. I help you identify investment opportunities, connect with local partners, and understand the market before you commit capital.",
    highlights: ["Market research", "Local partner introductions", "Investment analysis", "Sector insights"],
    href: "/contact",
    img: null,
    available: false,
    cta: "Enquire",
  },
];

export default function Services() {
  return (
    <>
    <Helmet>
      <title>Relocation & Real Estate Services in Brașov — Brașov Gateway</title>
      <meta name="description" content="Relocation, real estate, and company setup services in Brașov, Romania. Tailored support for foreigners who want to move, invest, or do business here." />
      <meta property="og:title" content="Relocation & Real Estate Services in Brașov — Brașov Gateway" />
      <meta property="og:description" content="Relocation, real estate, and company setup services in Brașov, Romania. Tailored support for foreigners who want to move, invest, or do business here." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/home-hero-brasov-v2_fd2b2f8b.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/services" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Relocation & Real Estate Services in Brașov — Brașov Gateway" />
      <link rel="canonical" href="https://brasovgateway.com/services" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": "https://brasovgateway.com/services",
          "name": "Brașov Gateway Services",
          "url": "https://brasovgateway.com/services",
          "description": "Professional services for foreigners in Brașov: real estate advisory, company setup, and relocation support.",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Real Estate Advisory",
              "url": "https://brasovgateway.com/services/real-estate"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Company Setup in Romania",
              "url": "https://brasovgateway.com/services/company-setup"
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "'Source Sans 3', sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Page Header */}
      <section className="pt-32 pb-16 px-4 bg-white border-b border-stone-100">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.2em] mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>
            What I offer
          </p>
          <h1 className="text-5xl md:text-6xl text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Services
          </h1>
          <p className="text-lg text-stone-500 max-w-2xl leading-relaxed">
            A single point of contact for everything you need to live, invest, or do business in Brașov. No hand-offs, no middlemen — just direct, honest help.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 px-4">
        <div className="container max-w-5xl space-y-6">
          {SERVICES.map((service, idx) => {
            const Icon = service.icon;
            const isEven = idx % 2 === 1;

            return (
              <div
                key={service.number}
                className="bg-white border border-stone-200 overflow-hidden"
                style={{ opacity: service.available ? 1 : 0.7 }}
              >
                <div className={`grid md:grid-cols-5 ${isEven ? "md:flex-row-reverse" : ""}`}>
                  {/* Image — only for available services */}
                  {service.img ? (
                    <div className={`md:col-span-2 relative h-48 md:h-auto overflow-hidden ${isEven ? "md:order-last" : ""}`}>
                      <img
                        src={service.img}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.15) 100%)" }} />
                    </div>
                  ) : (
                    <div
                      className={`md:col-span-2 relative h-48 md:h-auto flex items-center justify-center ${isEven ? "md:order-last" : ""}`}
                      style={{ backgroundColor: "oklch(0.96 0.005 60)" }}
                    >
                      <div className="text-center">
                        <Icon size={40} className="mx-auto mb-3" style={{ color: "oklch(0.65 0.01 60)" }} />
                        <span className="text-xs uppercase tracking-widest" style={{ color: "oklch(0.55 0.01 60)" }}>
                          Coming soon
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className={`md:col-span-3 p-5 md:p-10 flex flex-col justify-between ${isEven ? "md:order-first" : ""}`}>
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-mono text-stone-400">{service.number}</span>
                        {service.available ? (
                          <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                            Available
                          </span>
                        ) : (
                          <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1 flex items-center gap-1" style={{ color: "oklch(0.55 0.01 60)", backgroundColor: "oklch(0.93 0.005 60)" }}>
                            <Clock size={10} /> Coming soon
                          </span>
                        )}
                      </div>

                      <h2 className="text-2xl md:text-3xl text-stone-900 mb-1 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {service.title}
                      </h2>
                      <p className="text-sm text-stone-400 mb-4 uppercase tracking-wide">{service.subtitle}</p>

                      <p className="text-stone-600 leading-relaxed mb-6 text-sm md:text-base">
                        {service.description}
                      </p>

                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                        {service.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2 text-sm text-stone-500">
                            <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      {service.available && service.href ? (
                        <Link
                          href={service.href}
                          className="inline-flex items-center gap-2 px-6 py-3 text-white no-underline text-xs uppercase tracking-widest hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: "oklch(0.38 0.12 15)" }}
                        >
                          {service.cta} <ArrowRight size={13} />
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest cursor-not-allowed"
                          style={{ backgroundColor: "oklch(0.93 0.005 60)", color: "oklch(0.55 0.01 60)" }}
                        >
                          <Clock size={13} /> {service.cta}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 text-white" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.2em] mb-4 opacity-60">Not sure where to start?</p>
          <h2 className="text-3xl md:text-4xl mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Tell me what you're trying to do.<br />
            <em className="not-italic" style={{ color: "oklch(0.75 0.1 40)" }}>I'll tell you how I can help.</em>
          </h2>
          <p className="text-stone-400 mb-10 max-w-xl mx-auto leading-relaxed">
            A 30-minute conversation is usually enough to understand your situation and outline a clear path forward.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 text-white no-underline text-xs uppercase tracking-widest hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "oklch(0.38 0.12 15)" }}
          >
            Get in touch <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
